// src/components/CommentForm.js
import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postComment } from '../utils/api';
import axios from 'axios';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Send } from 'lucide-react';

const CommentForm = ({ articleId, refetchComments }) => {
  const [content, setContent] = useState('');
  const [error, setError] = useState(null);
  const queryClient = useQueryClient();

  // Fetch CSRF token
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/get-csrf-token/`, { 
      withCredentials: true 
    }).catch((err) => console.error('Failed to fetch CSRF token:', err));
  }, []);

  const mutation = useMutation({
    mutationFn: (content) => postComment(articleId, content),
    onSuccess: () => {
      setContent('');
      setError(null);
      refetchComments();
    },
    onError: (error) => {
      console.error('Comment submission error:', error);
      let errorMessage = 'Failed to post comment. Please try again.';
      
      if (error.response?.status === 405) {
        errorMessage = 'Invalid request method. Contact support.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Permission denied. Please log in.';
      } else if (error.response?.data?.errors) {
        errorMessage = JSON.stringify(error.response.data.errors);
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      setError(errorMessage);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (content.trim()) {
      mutation.mutate(content);
    }
  };

  return (
    <Card className="mb-10">
      <CardHeader>
        <CardTitle className="text-xl">Leave a Comment</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What are your thoughts on this article?"
            rows={4}
            className="resize-y min-h-[120px]"
          />

          <Button 
            type="submit" 
            className="w-full sm:w-auto flex items-center gap-2"
            disabled={mutation.isPending || !content.trim()}
          >
            {mutation.isPending ? (
              <>Posting...</>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Post Comment
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default CommentForm;
